import { MONITORED_AUTO_ENRICHMENT_SOURCE } from '@shared/constants/enrichment-source';

import type { SessionCriteria } from './session.domain';

/**
 * The three states an entity can be in before a run, in the order a session
 * works through them; and the order the pool bar draws them in, so the picture
 * and the cohort can never disagree.
 *
 * Here rather than in the panel's own domain because these strings are what a
 * run *records*: they travel to the database in `criteria` and are read back by
 * both features, so they are the shared vocabulary, not the panel's private one.
 */
export type BucketKey = 'new' | 'stale' | 'fresh';

export const BUCKET_ORDER: readonly BucketKey[] = ['new', 'stale', 'fresh'];

/** Sentinel standing for "no type set" in the type filter, and on the wire. */
export const UNSET_TYPE_VALUE = '__unset__';

/**
 * How each bucket reads once the run is over.
 *
 * Not the legend's wording: there, `up to date` is a row you may tick and the
 * warning lives beside it. Here the ticking already happened, so the fact worth
 * recording is that the freshness guard was overridden; a run of profiles
 * enriched last week is otherwise indistinguishable from one that had nothing to
 * do, and it cost a call each.
 */
const BUCKET_RECIPE_LABEL: Record<BucketKey, string> = {
  new: 'never enriched',
  stale: 'outdated',
  fresh: 'up to date, forced',
};

/**
 * The question a run answered, in one line: `HNWI · never enriched + outdated ·
 * first 200`.
 *
 * Reads what the run recorded rather than what it did: `target_ids` already says
 * who ran, and a row showing "99 targets" against a list of 412 cannot explain
 * itself without this.
 *
 * Empty for a run that recorded nothing, and the caller draws no line at all;
 * a dash or an "unknown" would turn silence into a claim. Anything it cannot
 * read is skipped rather than printed raw: an old recipe should degrade to the
 * part that still makes sense, never to noise.
 */
export function formatRecipe(criteria: SessionCriteria): string {
  const parts: string[] = [];

  // First, because it changes how the rest reads: nobody picked these targets,
  // the star did. Short on purpose; it shares one caption line with the count
  // in a column sized for the connector chain, and the detail page says the
  // same thing in full beside the requester.
  if (isAutomaticRun(criteria)) parts.push(AUTOMATIC_RUN_RECIPE);

  const types = criteria.types ?? [];
  if (types.length > 0) {
    parts.push(types.map((t) => (t === UNSET_TYPE_VALUE ? 'no type' : t)).join(', '));
  }

  const buckets = BUCKET_ORDER.filter((key) => (criteria.buckets ?? []).includes(key));
  if (buckets.length > 0) {
    parts.push(buckets.map((key) => BUCKET_RECIPE_LABEL[key]).join(' + '));
  }

  if (criteria.cap && criteria.cap > 0) parts.push(`first ${criteria.cap.toLocaleString()}`);
  if (criteria.currentOnly) parts.push('current employers only');

  return parts.join(' · ');
}

/** How an automatic run reads in the recipe line: `automatic · tracked profiles`. */
export const AUTOMATIC_RUN_RECIPE = 'automatic · tracked profiles';

/**
 * Whether the daily re-enrichment of tracked profiles created this run (#519),
 * as opposed to an admin. The pipeline still carries an admin (the one who
 * tracks the profiles) so without this the run is indistinguishable from one
 * they launched by hand.
 */
export function isAutomaticRun(criteria: SessionCriteria): boolean {
  return criteria.source === MONITORED_AUTO_ENRICHMENT_SOURCE;
}

/**
 * Whether a run recorded a question that can be asked again; what decides if
 * "Ask this again" is offered.
 *
 * The automatic marker alone does not count: the daily re-enrichment's question
 * ("the tracked profiles that went stale") is not one the session panel can
 * pose, and re-asking it would open the panel on no licence, no list and no
 * filter. Its own next run is tomorrow's.
 */
export function hasRecipe(criteria: SessionCriteria): boolean {
  return formatRecipe({ ...criteria, source: undefined }).length > 0;
}
